import { CATALOG_SUBJECT_EVERYONE, type ToolCatalogEntry } from "./types";

/** Splits a tool_ref into its toolkit / tool segments (`Toolkit.Tool` or `tk/tool`). */
const TOOL_REF_SEPARATOR = /[./]/;

/**
 * The toolkit ("server") a tool_ref belongs to — the segment before the first
 * `.` or `/`, with any `@version` suffix stripped. `Github.CreateIssue` and
 * `github@1/create_issue@1` both resolve to their toolkit name.
 */
export const toolkitOf = (toolRef: string): string => {
  const head = toolRef.split(TOOL_REF_SEPARATOR)[0] ?? toolRef;
  return head.split("@")[0] || toolRef;
};

/** The tool's short name — everything after the toolkit segment. */
export const toolNameOf = (toolRef: string): string => {
  const parts = toolRef.split(TOOL_REF_SEPARATOR);
  return parts.length > 1 ? parts.slice(1).join(".") : toolRef;
};

/** A toolkit ("server") and its published entries within one audience. */
export type CatalogToolkitGroup = {
  toolkit: string;
  entries: ToolCatalogEntry[];
};

/** One audience (IdP group or everyone) and the toolkits published to it. */
export type CatalogAudienceGroup = {
  subject: string;
  toolkits: CatalogToolkitGroup[];
  total: number;
};

/**
 * Group catalog entries by audience (`subject`), then by toolkit within each
 * audience. Named audiences sort alphabetically; the everyone (`*`) audience
 * always sorts last so the broadest grant reads as the catch-all at the bottom.
 */
export const groupBySubjectThenToolkit = (
  entries: ToolCatalogEntry[]
): CatalogAudienceGroup[] => {
  const bySubject = new Map<string, ToolCatalogEntry[]>();
  for (const entry of entries) {
    const bucket = bySubject.get(entry.subject);
    if (bucket) {
      bucket.push(entry);
    } else {
      bySubject.set(entry.subject, [entry]);
    }
  }
  return [...bySubject.entries()]
    .map(([subject, subjectEntries]) => ({
      subject,
      toolkits: groupByToolkit(subjectEntries),
      total: subjectEntries.length,
    }))
    .sort((a, b) => {
      if (a.subject === CATALOG_SUBJECT_EVERYONE) {
        return 1;
      }
      if (b.subject === CATALOG_SUBJECT_EVERYONE) {
        return -1;
      }
      return a.subject.localeCompare(b.subject);
    });
};

/** Catalog entries grouped by toolkit, each group's entries kept in list order. */
export const groupByToolkit = (
  entries: ToolCatalogEntry[]
): CatalogToolkitGroup[] => {
  const groups = new Map<string, ToolCatalogEntry[]>();
  for (const entry of entries) {
    const key = toolkitOf(entry.tool_ref);
    const bucket = groups.get(key);
    if (bucket) {
      bucket.push(entry);
    } else {
      groups.set(key, [entry]);
    }
  }
  return [...groups.entries()]
    .map(([toolkit, groupEntries]) => ({ toolkit, entries: groupEntries }))
    .sort((a, b) => a.toolkit.localeCompare(b.toolkit));
};

/** Filter catalog entries by a case-insensitive substring match on subject or tool_ref. */
export const filterCatalogBySearch = (
  entries: ToolCatalogEntry[],
  query: string
): ToolCatalogEntry[] => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return entries;
  }
  return entries.filter((entry) =>
    [entry.subject, entry.tool_ref].some((value) =>
      value.toLowerCase().includes(normalized)
    )
  );
};
